import { useState } from 'react';
import type { AdminSummaryDto, Locale, TableOwner } from '@iac/contracts';
import { useDict } from '../../i18n/dictionary';
import { shell } from '../../i18n/shell';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import { DataTable, Td, Tr, type Column } from '../../components/DataTable';
import { DonutChart } from '../../components/DonutChart';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { Stat, StatGrid } from '../../components/Stat';
import { useAdminOverview } from './api';
import { adminDict } from './i18n';

type Sort = { key: 'size' | 'name'; dir: 'asc' | 'desc' };

/** Size sorts biggest-first, name A–Z — what a click on a fresh column means. */
const DEFAULT_DIR: Record<Sort['key'], Sort['dir']> = {
  size: 'desc',
  name: 'asc',
};

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** From this shown percentage up, the used share is drawn in the danger tone. */
const DISK_FULL_PERCENT = 90;

function formatBytes(bytes: number, locale: Locale): string {
  const exp = Math.min(
    bytes < 1024 ? 0 : Math.floor(Math.log(bytes) / Math.log(1024)),
    UNITS.length - 1,
  );
  const digits = exp === 0 ? 0 : 1;
  const value = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(bytes / 1024 ** exp);
  return `${value} ${UNITS[exp]}`;
}

export function AdminOverview() {
  const t = useDict(adminDict);
  usePageTitle(t.overviewTitle);
  const { data: summary } = useAdminOverview();

  if (!summary) return <PageHeader title={t.overview} />;

  return (
    <>
      <PageHeader title={t.overview} />

      <h2 className="heading-section">{t.application}</h2>
      <StatGrid>
        <Stat label={t.userCount} value={summary.userCount} />
        <Stat label={t.appCount} value={summary.appCount} />
      </StatGrid>

      <h2 className="heading-section">{t.storage}</h2>
      <DiskUsage disk={summary.disk} />

      <DatabaseUsage database={summary.database} />
    </>
  );
}

/**
 * Used against free, with the used share in the hole. Danger follows the
 * rounded percentage, so the number shown never sits on both sides of it.
 */
function DiskUsage({ disk }: { disk: AdminSummaryDto['disk'] }) {
  const t = useDict(adminDict);
  const { locale } = useLocale();
  const used = disk.totalBytes - disk.freeBytes;
  const percent = Math.round((used / disk.totalBytes) * 100);
  const full = percent >= DISK_FULL_PERCENT;

  return (
    <Panel as="div" className="disk-chart">
      <DonutChart
        label={t.diskChart}
        slices={[
          {
            label: t.diskUsedSlice,
            value: used,
            tone: full ? 'danger' : undefined,
          },
          { label: t.diskFreeSlice, value: disk.freeBytes },
        ]}
        center={
          <span className="disk-chart-center">
            <span className={full ? 'disk-chart-center-danger' : undefined}>
              {new Intl.NumberFormat(locale, { style: 'percent' }).format(
                percent / 100,
              )}
            </span>
            <span className="disk-chart-center-caption">
              {t.diskOf(formatBytes(disk.totalBytes, locale))}
            </span>
          </span>
        }
        formatValue={(value) => formatBytes(value, locale)}
      />
    </Panel>
  );
}

/**
 * Sorted here rather than by the API: seven rows, and the name order depends on
 * the language the screen renders in. "Other" is not an owner, so it sits
 * below whatever order is chosen.
 */
function DatabaseUsage({
  database,
}: {
  database: AdminSummaryDto['database'];
}) {
  const t = useDict(adminDict);
  const appNames = useDict(shell).appNames;
  const { locale } = useLocale();
  const [sort, setSort] = useState<Sort>({ key: 'size', dir: 'desc' });

  const ownerName = (owner: TableOwner) =>
    owner === 'core' ? t.coreOwner : appNames[owner];
  const collator = new Intl.Collator(locale);
  const sign = sort.dir === 'asc' ? 1 : -1;
  const owners = [...database.owners].sort((a, b) =>
    sort.key === 'size'
      ? sign * (a.bytes - b.bytes)
      : sign * collator.compare(ownerName(a.owner), ownerName(b.owner)),
  );
  const rows = new Intl.NumberFormat(locale);

  const toggle = (key: Sort['key']) =>
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: DEFAULT_DIR[key] },
    );
  const column = (
    key: Sort['key'],
    label: string,
    align?: Column['align'],
  ): Column => ({
    label,
    onSort: () => toggle(key),
    align,
    sort:
      sort.key === key
        ? sort.dir === 'asc'
          ? 'ascending'
          : 'descending'
        : undefined,
  });

  return (
    <>
      <h2 className="heading-section">{t.database}</h2>
      <StatGrid>
        <Stat
          label={t.databaseSize}
          value={formatBytes(database.totalBytes, locale)}
        />
      </StatGrid>
      <DataTable
        columns={[
          column('name', t.owner),
          column('size', t.size, 'end'),
          { label: t.rows, align: 'end' },
        ]}
      >
        {owners.map(({ owner, bytes, rows: count }) => (
          <Tr key={owner}>
            <Td emphasis>{ownerName(owner)}</Td>
            <Td numeric>{formatBytes(bytes, locale)}</Td>
            <Td numeric>{rows.format(count)}</Td>
          </Tr>
        ))}
        <Tr>
          <Td emphasis>{t.otherOwner}</Td>
          <Td numeric>{formatBytes(database.otherBytes, locale)}</Td>
          <Td>—</Td>
        </Tr>
      </DataTable>
    </>
  );
}
